import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, isToday, isWeekend } from "date-fns";
import UserInitialsAvatar from "../../../utils/userInitialAvatar";
import { getProfilePictureUrl } from "../../../utils/imageUtils";
import AbsenceBar from "./AbsenceBar";
import { assignLanes, getBarPlacement, getDateLocale, getFullName, getMonthDays, toDateKey } from "./teamOverviewUtils";

const dayClassName = (day, holiday) =>
  [
    "team-overview__day",
    isWeekend(day) && "team-overview__day--weekend",
    holiday && "team-overview__day--holiday",
    isToday(day) && "team-overview__day--today",
  ]
    .filter(Boolean)
    .join(" ");

const PersonCell = ({ user, laneCount }) => {
  const name = getFullName(user);
  const pictureUrl = getProfilePictureUrl(user);

  return (
    <div className="team-overview__person" style={{ gridRow: `1 / span ${laneCount}` }}>
      {pictureUrl ? (
        <img className="team-overview__avatar" src={pictureUrl} alt="" />
      ) : (
        <UserInitialsAvatar
          first_name={user.first_name}
          last_name={user.last_name}
          style={{ width: 28, height: 28, fontSize: "0.7rem", flexShrink: 0 }}
        />
      )}
      <span className="team-overview__name" title={name}>
        {name}
      </span>
    </div>
  );
};

const PersonRow = ({ user, days, month, gridTemplateColumns }) => {
  const name = getFullName(user);

  const holidaysByDay = useMemo(
    () => new Map((user.holidays || []).map((holiday) => [holiday.date, holiday.label])),
    [user.holidays],
  );

  const { items, laneCount } = useMemo(() => {
    const placed = (user.absences || [])
      .map((absence) => ({ absence, placement: getBarPlacement(absence, month) }))
      .filter((item) => item.placement);
    return assignLanes(placed);
  }, [user.absences, month]);

  return (
    <div
      className="team-overview__row"
      style={{ gridTemplateColumns, gridTemplateRows: `repeat(${laneCount}, minmax(var(--to-lane-height), 1fr))` }}
    >
      <PersonCell user={user} laneCount={laneCount} />
      {days.map((day, index) => {
        const holiday = holidaysByDay.get(toDateKey(day));
        return (
          <div
            key={index}
            className={dayClassName(day, holiday)}
            style={{ gridColumn: index + 2, gridRow: `1 / span ${laneCount}` }}
            title={holiday || undefined}
          />
        );
      })}
      {items.map(({ absence, placement, lane }) => (
        <AbsenceBar key={absence.id} absence={absence} placement={placement} lane={lane} personName={name} />
      ))}
    </div>
  );
};

const TeamOverviewGrid = ({ month, users }) => {
  const { t, i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const days = useMemo(() => getMonthDays(month), [month]);
  const gridTemplateColumns = `var(--to-name-width) repeat(${days.length}, minmax(var(--to-day-min-width), 1fr))`;

  return (
    <div className="team-overview__scroller">
      <div className="team-overview__grid" role="region" aria-label={format(month, "LLLL yyyy", { locale })}>
        <div className="team-overview__row team-overview__header" style={{ gridTemplateColumns }}>
          <div className="team-overview__person team-overview__person--header">
            {t("components.Calendar.teamOverview.person")}
          </div>
          {days.map((day, index) => (
            <div
              key={index}
              className={dayClassName(day, false)}
              aria-current={isToday(day) ? "date" : undefined}
            >
              <span className="team-overview__weekday">{format(day, "EEEEE", { locale })}</span>
              <span className="team-overview__daynum">{day.getDate()}</span>
            </div>
          ))}
        </div>
        {users.map((user) => (
          <PersonRow key={user.id} user={user} days={days} month={month} gridTemplateColumns={gridTemplateColumns} />
        ))}
      </div>
    </div>
  );
};

export default TeamOverviewGrid;
